import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { ReactComponent as ArrowIcon } from 'common-assets/assets/icons/chevron-right-nav.svg';
import { useTranslation } from 'react-i18next';
import { getKeyboardKey } from '../../../wcag/getKeyboardKey';
import { Checkbox } from '../../../Checkbox/Checkbox';
import { Radio } from '../../../Radio/Radio';
import { Button } from '../../../Button/Button';
import ModalCore, { ModalType } from '../../../Modal/ModalCore';
import { Badge } from '../../../Badge/Badge';
import { slugify } from 'common-react';

import useStyles from './CheckboxesModalList.style';

export interface CheckboxesModalListProps {
  title: string;
  options?: string[];
  value?: string[];
  onChange?: (value: string[]) => void;
  valueToLabel?: (value: string) => string;
  variant?: 'checkbox' | 'radio' | 'empty';
  isModalOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onSubmit?: () => void;
  renderButtonLabel?: (value: string[]) => string;
  renderMobileTopBar?: () => React.ReactNode;
  children?: React.ReactNode;
  disableFocusTrap?: boolean;
}

export const CheckboxesModalList = forwardRef<HTMLButtonElement | null, CheckboxesModalListProps>(({
  children,
  title,
  options,
  value,
  onChange,
  valueToLabel,
  isModalOpen,
  onClose,
  onOpen,
  onSubmit,
  variant = 'checkbox',
  renderButtonLabel,
  renderMobileTopBar,
  disableFocusTrap = false
}, ref) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const [isModalWithOptionsOpen, setIsModalWithOptionsOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string[]>(value || []);

  useEffect(() => {
    setTempValue(value || []);
  }, [value]);

  const autoFocusRef = useRef<HTMLButtonElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const finalButtonRef = ref || buttonRef;

  useEffect(() => {
    if (autoFocusRef.current && isModalWithOptionsOpen) {
        autoFocusRef.current?.focus();
    }
  }, [isModalWithOptionsOpen]);

  const handleClose = () => {
    setIsModalWithOptionsOpen(false);
    setTempValue(value || []);
    onClose?.();
  }

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalWithOptionsOpen(false);
    onChange?.(tempValue);
    onSubmit?.();
    onClose?.();
  }

  const handleSubmitButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const { isTabKey } = getKeyboardKey(event.nativeEvent);
    if (isTabKey && event.shiftKey) {
      return;
    } else if (isTabKey) {
      event.preventDefault();
      event.stopPropagation();
      autoFocusRef.current?.focus();
    } else {
      return;
    }
  }

  const handleCloseButtonKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { isTabKey } = getKeyboardKey(event.nativeEvent);
    if (isTabKey && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      buttonRef.current?.focus();
    }
  }

  const buttonLabelValue = !!value && value.length > 0 ? value.length : null;
  return (
    <>
      <button className={c.button} onClick={() => {
        setIsModalWithOptionsOpen(true);
        onOpen?.();
      }}>
        {renderButtonLabel ? renderButtonLabel(value || []) : title}
        <span className={c.buttonLabelValueWrapper}>
          {buttonLabelValue && <Badge badgeContent={buttonLabelValue} />}
          <ArrowIcon className={c.arrowIcon} />
        </span>
      </button>
      {(isModalWithOptionsOpen || isModalOpen) && <ModalCore
        handleClose={handleClose}
        disableFocusTrap={disableFocusTrap}
        ref={ref}
        onBackButtonKeyDown={handleCloseButtonKeyDown}
        type={ModalType.withHeader}
        title={title}
        titleType="left"
        renderMobileTopBar={renderMobileTopBar}
      >
        <div className={c.wrapper}>
          <div>
            {children}
            {variant !== 'empty' && <ul className={c.list}>
              {options?.map(option => (
                variant === 'checkbox'
                  ? <CheckboxesModalListItem
                    key={slugify(option)}
                    label={option}
                    value={tempValue}
                    onChange={setTempValue}
                    valueToLabel={valueToLabel}
                  />
                  : <RadioModalListItem
                    key={slugify(option)}
                    label={option}
                    value={tempValue}
                    onChange={setTempValue}
                    valueToLabel={valueToLabel}
                  />
              ))}
            </ul>}
          </div>
          <Button onClick={handleSubmit} ref={finalButtonRef} onKeyDown={handleSubmitButtonKeyDown}>
            {t('checkboxes-modal-list.button-label')}
          </Button>
        </div>
      </ModalCore>}
    </>
  );
});

CheckboxesModalList.displayName = 'CheckboxesModalList';

interface CheckboxesModalListItemProps {
  label: string;
  value: string[];
  onChange?: (value: string[]) => void;
  valueToLabel?: (value: string) => string;
}

export const CheckboxesModalListItem: React.FC<CheckboxesModalListItemProps> = ({ label, value, onChange, valueToLabel }) => {
  const c = useStyles({});
  const slugLabel = slugify(label);

  const handleChange = (checked: boolean) => {
    onChange && onChange(checked
      ? [...value, label]
      : [...value].filter(item => item !== label));
  };

  return (
    <li className={c.checkBoxWrapper} key={slugLabel} data-testid={slugLabel}>
      <label htmlFor={slugLabel} className={c.label}>{valueToLabel ? valueToLabel(label) : label}</label>
      <Checkbox
        checked={value.indexOf(label) > -1}
        onChange={(_, checked) => handleChange(checked)}
        className={c.checkBox}
        id={slugLabel}
      />
    </li>
  );
};

interface RadioModalListItemProps {
  label: string;
  value: string[];
  onChange?: (value: string[]) => void;
  valueToLabel?: (value: string) => string;
}

export const RadioModalListItem: React.FC<RadioModalListItemProps> = ({ label, value, onChange, valueToLabel }) => {
  const c = useStyles({});
  const slugLabel = slugify(label);

  const handleChange = (checked: boolean) => {
    if (checked) {
      onChange && onChange([label]);
    }
  };

  return (
    <li className={c.checkBoxWrapper} key={slugLabel} data-testid={slugLabel}>
      <label htmlFor={slugLabel} className={c.label}>{valueToLabel ? valueToLabel(label) : label}</label>
      <Radio
        checked={value.indexOf(label) > -1}
        onChange={(_, checked) => handleChange(checked)}
        className={c.checkBox}
        id={slugLabel}
      />
    </li>
  );
};
